/**
 * Session-specific storage utility for per-tab authentication
 * This prevents authentication conflicts when multiple roles are logged in across different tabs
 */

class SessionStorage {
  constructor() {
    // Key prefixes for session-specific storage
    this.SESSION_KEY = 'app_session_id';
    this.TOKEN_KEY = 'authToken';
    this.USER_KEY = 'user';
    this.LOGIN_TIME_KEY = 'loginTime';
    
    // Initialize session storage (persist across reloads in the same tab)
    this.initializeSession();
    
    // Listen for storage events from other tabs
    this.setupCrossTabListener();
  }

  /**
   * Generate a unique session ID for this tab
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize session by storing the session ID
   */
  initializeSession() {
    // Use native sessionStorage to persist per-tab across refreshes
    // Falls back to a generated ID if none exists for this tab yet
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const existingSessionId = window.sessionStorage.getItem(this.SESSION_KEY);
        if (existingSessionId) {
          this.sessionId = existingSessionId;
        } else {
          this.sessionId = this.generateSessionId();
          window.sessionStorage.setItem(this.SESSION_KEY, this.sessionId);
        }
      } else {
        // Very rare environments without window/sessionStorage
        this.sessionId = this.generateSessionId();
      }
    } catch (e) {
      // If sessionStorage is unavailable (e.g., privacy mode), fall back gracefully
      this.sessionId = this.generateSessionId();
    }
  }

  /**
   * Get the session-specific key for a given storage key
   */
  getSessionKey(key) {
    return `${key}_${this.sessionId}`;
  }

  /**
   * Store an item in session-specific storage
   */
  setItem(key, value) {
    const sessionKey = this.getSessionKey(key);
    localStorage.setItem(sessionKey, value);
    
    // Also store metadata for cross-tab communication
    this.broadcastSessionChange(key, value);
  }

  /**
   * Get an item from session-specific storage
   */
  getItem(key) {
    const sessionKey = this.getSessionKey(key);
    return localStorage.getItem(sessionKey);
  }

  /**
   * Remove an item from session-specific storage
   */
  removeItem(key) {
    const sessionKey = this.getSessionKey(key);
    localStorage.removeItem(sessionKey);
    
    // Broadcast the removal
    this.broadcastSessionChange(key, null);
  }

  /**
   * Remove all stored data for this session
   */
  clearSession() {
    this.removeItem(this.TOKEN_KEY);
    this.removeItem(this.USER_KEY);
    this.removeItem(this.LOGIN_TIME_KEY);
  }

  /**
   * Broadcast session changes to other components in the same tab
   * This allows the app to react to storage changes from other tabs
   */
  broadcastSessionChange(key, value) {
    // Store a change marker that components can listen for
    const changeMarker = {
      key,
      value,
      sessionId: this.sessionId,
      timestamp: Date.now()
    };
    localStorage.setItem('session_change', JSON.stringify(changeMarker));
  }

  /**
   * Setup listener for cross-tab storage events
   * This detects when another tab changes localStorage
   */
  setupCrossTabListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        // Only respond to changes in our session storage keys
        if (e.key === 'session_change' && e.newValue) {
          try {
            const changeMarker = JSON.parse(e.newValue);
            
            // Ignore events from this tab
            if (changeMarker.sessionId !== this.sessionId) {
              console.log('Cross-tab storage change detected:', changeMarker.key);
              
              // Dispatch a custom event for the app to handle
              window.dispatchEvent(new CustomEvent('authStorageChange', {
                detail: changeMarker
              }));
            }
          } catch (error) {
            console.error('Error parsing storage change event:', error);
          }
        }
      });
    }
  }

  /**
   * Get the current session ID
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * Check if there's an active session in another tab
   */
  hasOtherActiveSession() {
    const allKeys = Object.keys(localStorage);
    const sessionKeys = allKeys.filter(key => 
      key.startsWith(this.TOKEN_KEY + '_session_') && 
      !key.endsWith(this.sessionId)
    );
    return sessionKeys.length > 0;
  }
}

// Export a singleton instance
const sessionStorage = new SessionStorage();

export default sessionStorage;

